/**
 * @fileoverview Unified client error log + recovery entrypoint.
 * @description Classifies, logs (with recovery metadata), and attempts one-shot
 * reload for transient faults. Callers should prefer this over calling
 * `logClientError` and `maybeRecoverFromClientError` separately.
 *
 * @module lib/client-error-handling
 */

import { classifyClientError } from '~/lib/client-error-classification'
import {
  evaluateClientErrorRecovery,
  markClientErrorReloadAttempted,
  shouldAutoRecoverWithBoundaryUi,
  type ClientErrorRecoveryResult,
} from '~/lib/client-error-recovery'
import {
  logClientError,
  resetClientErrorDedupeForTests,
  shouldIgnoreClientError,
} from '~/lib/logger-client'

/** Short TTL dedupe window for duplicate handler + boundary emissions. */
const LOG_DEDUPE_TTL_MS = 2_000

const recentClientErrorLogs = new Map<string, number>()

/**
 * Clears recent log dedupe state (unit tests only). Resets BOTH layers — this
 * handler-level TTL map and `logClientError`'s per-session fingerprint map —
 * so "the dedupe was reset" means the next identical fault POSTs again.
 */
export function resetClientErrorLogDedupeForTests(): void {
  recentClientErrorLogs.clear()
  resetClientErrorDedupeForTests()
}

/**
 * Builds a dedupe key so the same fault is not POSTed twice when both the
 * window handler and an error boundary observe it.
 *
 * @param category - Classified error category.
 * @param message - Normalized error message.
 * @returns Dedupe key including pathname when available.
 */
function buildClientErrorDedupeKey(
  category: string,
  message: string,
): string {
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : ''
  return `${category}:${message}:${pathname}`
}

/**
 * Whether an identical client error was logged within the dedupe TTL.
 *
 * @param key - Dedupe key from {@link buildClientErrorDedupeKey}.
 * @returns `true` when an identical log was recently recorded.
 */
function wasClientErrorRecentlyLogged(key: string): boolean {
  const lastLoggedAt = recentClientErrorLogs.get(key)
  return (
    lastLoggedAt !== undefined &&
    Date.now() - lastLoggedAt < LOG_DEDUPE_TTL_MS
  )
}

/**
 * Records that a client error was logged for dedupe purposes.
 *
 * @param key - Dedupe key from {@link buildClientErrorDedupeKey}.
 */
function recordClientErrorLog(key: string): void {
  recentClientErrorLogs.set(key, Date.now())
}

export interface HandleClientErrorOptions {
  /** Optional reload override (tests only). */
  reload?: () => void
  /**
   * When `false`, log only — no reload guard write and no hard navigation.
   * When omitted, defaults to auto-recover for all recoverable categories.
   */
  recover?: boolean
  /**
   * When `true`, only `chunk_load` faults auto-reload; `dom_reconciliation`
   * shows boundary fallback UI. Ignored when `recover` is set explicitly.
   */
  boundaryRecover?: boolean
}

/**
 * Classify, log, and attempt auto-recovery for a client-side error.
 *
 * @param error - Thrown error or rejection reason.
 * @param context - Extra fields merged into the `/api/log` context payload.
 * @param options.reload - Optional reload override (tests only).
 * @param options.recover - When `false`, skip auto-reload (default `true`).
 * @returns Recovery outcome (including whether a reload was triggered).
 */
export function handleClientError(
  error: unknown,
  context: Record<string, unknown> = {},
  options: HandleClientErrorOptions = {},
): ClientErrorRecoveryResult {
  const normalizedError =
    error instanceof Error ? error : new Error(String(error))

  if (
    shouldIgnoreClientError(
      normalizedError.message,
      normalizedError.stack,
    )
  ) {
    return {
      reloaded: false,
      classification: classifyClientError(normalizedError),
      recoveryBlocked: false,
    }
  }

  const classification = classifyClientError(normalizedError)
  const recover =
    options.recover ??
    (options.boundaryRecover
      ? shouldAutoRecoverWithBoundaryUi(classification)
      : true)

  const evaluation = evaluateClientErrorRecovery(normalizedError, {
    ...options,
    recover,
  })

  const intendsReload =
    recover &&
    evaluation.classification.recoverable &&
    !evaluation.recoveryBlocked &&
    !evaluation.recoveryStorageUnavailable &&
    typeof window !== 'undefined'

  let recoveryAttempted = false
  let recoveryStorageUnavailable =
    evaluation.recoveryStorageUnavailable ?? false
  let willReload = intendsReload

  if (intendsReload) {
    const deploymentId = evaluation.deploymentId
    const category = evaluation.classification.errorCategory
    const guardPersisted = markClientErrorReloadAttempted(deploymentId, category)
    if (guardPersisted) {
      recoveryAttempted = true
    } else {
      recoveryStorageUnavailable = true
      willReload = false
    }
  }

  const dedupeKey = buildClientErrorDedupeKey(
    evaluation.classification.errorCategory,
    normalizedError.message,
  )

  if (!wasClientErrorRecentlyLogged(dedupeKey)) {
    recordClientErrorLog(dedupeKey)
    logClientError(normalizedError, {
      ...context,
      recoveryAttempted,
      recoveryBlocked: evaluation.recoveryBlocked ?? false,
      recoveryStorageUnavailable,
    })
  }

  if (!willReload) {
    return {
      reloaded: false,
      classification: evaluation.classification,
      deploymentId: evaluation.deploymentId,
      ...(evaluation.recoveryBlocked ? { recoveryBlocked: true } : {}),
      ...(recoveryStorageUnavailable ? { recoveryStorageUnavailable: true } : {}),
    }
  }

  const reload = options.reload ?? (() => window.location.reload())
  reload()

  return {
    reloaded: true,
    classification: evaluation.classification,
    deploymentId: evaluation.deploymentId,
  }
}
